package floris.graphics.backoffice.pages.admin.settings

import floris.graphics.models.domain.models.user.User
import floris.graphics.models.dto.requests.user.UpdatePassword
import floris.graphics.models.dto.requests.user.UpdateUser
import floris.graphics.models.dto.requests.user.isPasswordStrong
import floris.graphics.repo.UserRepo
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class SettingsViewModel(
    private val userRepo: UserRepo = UserRepo,
    private val coroutineScope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
) {
    private val _state = MutableStateFlow(SettingsState())
    val state = _state.asStateFlow()

    private val _eventFlow = Channel<SettingsUiEvent>()
    val eventFlow = _eventFlow.receiveAsFlow()

    fun onEvent(event: SettingsEvent) = coroutineScope.launch {
        when (event) {
            is SettingsEvent.LoadInitialData -> {
                val currentUser = userRepo.getCurrentUser()
                _state.update { it.copy(currentUser = currentUser, fullName = currentUser?.fullName, userName = currentUser?.username ?: "") }
            }

            is SettingsEvent.UpdateFullName -> _state.update { it.copy(fullName = event.fullName) }
            is SettingsEvent.UpdateEmail -> _state.update { it.copy(userName = event.email) }
            is SettingsEvent.Update -> {
                val state = _state.value
                val updateUser = UpdateUser(fullName = state.fullName, username = state.userName)
                val result = userRepo.updateCurrentUser(updateUser)

                if (result != null) {
//                    _eventFlow.send(SettingsUiEvent.SuccessfulUpdate)
                    _state.update { it.copy(currentUser = result, fullName = result.fullName, userName = result.username) }
                } else {
                    _eventFlow.send(SettingsUiEvent.ShowError)
                }
            }

            is SettingsEvent.ToggleChangingPassword -> _state.update { it.copy(isChangingPassword = !it.isChangingPassword) }
            is SettingsEvent.UpdatePassword -> {
                val state = _state.value
                val updatePassword = UpdatePassword(
                    state.oldPassword,
                    state.newPassword,
                    state.confirmNewPassword
                )
                val result = userRepo.updatePasswordCurrentUser(updatePassword)

                if (result != null) {
                    _state.update { it.copy(isChangingPassword = false, oldPassword = "", newPassword = "", confirmNewPassword = "") }
                } else {
                    _eventFlow.send(SettingsUiEvent.ShowError)
                }
            }

            is SettingsEvent.ChangeConfirmNewPassword -> _state.update { it.copy(confirmNewPassword = event.confirmNewPassword) }
            is SettingsEvent.ChangeNewPassword -> _state.update { it.copy(newPassword = event.newPassword) }
            is SettingsEvent.ChangeOldPassword -> _state.update { it.copy(oldPassword = event.oldPassword) }
        }
    }

    init {
        onEvent(SettingsEvent.LoadInitialData)
    }
}

data class SettingsState(
    val currentUser: User? = null,
    val fullName: String? = null,
    val userName: String = "",
    val oldPassword: String = "",
    val newPassword: String = "",
    val confirmNewPassword: String = "",
    val errorText: String = "",
    val isChangingPassword: Boolean = false
) {
    private val fullNameChanged = currentUser?.fullName != fullName
    private val userNameChanged = currentUser?.username != userName

    val hasChanged: Boolean
        get() = fullNameChanged || userNameChanged

    val validNewPassword: Boolean
        get() = oldPassword.isNotBlank() && newPassword.isNotBlank() && newPassword.isPasswordStrong && newPassword == confirmNewPassword
}

sealed class SettingsEvent {
    data object LoadInitialData : SettingsEvent()
    data class UpdateFullName(val fullName: String) : SettingsEvent()
    data class UpdateEmail(val email: String) : SettingsEvent()
    data object Update : SettingsEvent()
    data object ToggleChangingPassword : SettingsEvent()
    data object UpdatePassword : SettingsEvent()
    data class ChangeOldPassword(val oldPassword: String) : SettingsEvent()
    data class ChangeNewPassword(val newPassword: String) : SettingsEvent()
    data class ChangeConfirmNewPassword(val confirmNewPassword: String) : SettingsEvent()
}

sealed class SettingsUiEvent {
    data class Navigate(val route: String) : SettingsUiEvent()
    data object ShowError : SettingsUiEvent()
}
